import { useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Card } from "../components/ui/card";
import { ArrowLeft } from "lucide-react";
import Footer from "../components/Footer";

export default function CookiePolicy() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <header className="border-b border-gray-200 bg-white px-6 py-4 shadow-sm">
        <div className="max-w-6xl mx-auto flex items-center gap-6">
          <div className="logo-placeholder"></div>
          <Button variant="ghost" onClick={() => navigate('/')} className="text-gray-600 hover:text-gray-900">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Home
          </Button>
        </div>
      </header>

      <div className="flex-1 p-6 bg-gray-50">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-8">
            <h1 className="text-4xl font-bold text-gray-900 mb-2">Cookie Policy</h1>
            <p className="text-gray-600">Last updated: December 2025</p>
          </div>

          <Card className="bg-white border-gray-200 shadow-md p-8">
            <div className="prose prose-gray max-w-none space-y-6">
              <section>
                <h2 className="text-2xl font-bold text-gray-900 mb-4">1. What Are Cookies?</h2>
                <p className="text-gray-700">
                  Cookies are small text files placed on your device when you visit our website. They help us provide a better user experience, remember your preferences, and understand how you interact with our platform.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-gray-900 mb-4">2. Types of Cookies We Use</h2>
                
                <h3 className="text-xl font-semibold text-gray-900 mb-3">2.1 Essential Cookies</h3>
                <p className="text-gray-700 mb-2">These cookies are necessary for the website to function:</p>
                <ul className="list-disc pl-6 space-y-2 text-gray-700">
                  <li><strong>Authentication:</strong> Keep you logged in (Firebase tokens)</li>
                  <li><strong>Security:</strong> Protect against fraud and unauthorized access</li>
                  <li><strong>Session Management:</strong> Maintain your session state</li>
                </ul>

                <h3 className="text-xl font-semibold text-gray-900 mb-3 mt-4">2.2 Functionality Cookies</h3>
                <ul className="list-disc pl-6 space-y-2 text-gray-700">
                  <li><strong>Preferences:</strong> Remember your settings and choices</li>
                  <li><strong>Language:</strong> Store your language preference</li>
                  <li><strong>Theme:</strong> Remember your display preferences</li>
                </ul>

                <h3 className="text-xl font-semibold text-gray-900 mb-3 mt-4">2.3 Analytics Cookies</h3>
                <ul className="list-disc pl-6 space-y-2 text-gray-700">
                  <li><strong>Usage Analytics:</strong> Understand how you use our platform</li>
                  <li><strong>Performance Monitoring:</strong> Identify and fix technical issues</li>
                  <li><strong>Feature Usage:</strong> See which features are most popular</li>
                </ul>

                <h3 className="text-xl font-semibold text-gray-900 mb-3 mt-4">2.4 Payment Cookies</h3>
                <p className="text-gray-700">
                  Stripe (our payment processor) may set cookies to securely process payments and prevent fraud.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-gray-900 mb-4">3. Third-Party Cookies</h2>
                <p className="text-gray-700 mb-3">We use the following third-party services that may set cookies:</p>
                <ul className="list-disc pl-6 space-y-2 text-gray-700">
                  <li><strong>Firebase (Google):</strong> Authentication and user management</li>
                  <li><strong>Stripe:</strong> Payment processing</li>
                  <li><strong>Analytics Services:</strong> Usage tracking and insights</li>
                </ul>
                <p className="text-gray-700 mt-3">
                  These services have their own privacy and cookie policies. We recommend reviewing them:
                </p>
                <ul className="list-disc pl-6 space-y-1 text-gray-700 mt-2">
                  <li>Firebase: https://firebase.google.com/support/privacy</li>
                  <li>Stripe: https://stripe.com/privacy</li>
                </ul>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-gray-900 mb-4">4. How Long Do Cookies Last?</h2>
                <ul className="list-disc pl-6 space-y-2 text-gray-700">
                  <li><strong>Session Cookies:</strong> Deleted when you close your browser</li>
                  <li><strong>Persistent Cookies:</strong> Remain until expiration or manual deletion</li>
                  <li><strong>Authentication Cookies:</strong> Typically last 30 days</li>
                  <li><strong>Analytics Cookies:</strong> May last up to 2 years</li>
                </ul>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-gray-900 mb-4">5. Managing Cookies</h2>
                
                <h3 className="text-xl font-semibold text-gray-900 mb-3">5.1 Browser Settings</h3>
                <p className="text-gray-700 mb-2">You can control cookies through your browser:</p>
                <ul className="list-disc pl-6 space-y-2 text-gray-700">
                  <li><strong>Chrome:</strong> Settings → Privacy and security → Cookies</li>
                  <li><strong>Firefox:</strong> Options → Privacy & Security → Cookies</li>
                  <li><strong>Safari:</strong> Preferences → Privacy → Cookies</li>
                  <li><strong>Edge:</strong> Settings → Privacy → Cookies</li>
                </ul>

                <h3 className="text-xl font-semibold text-gray-900 mb-3 mt-4">5.2 Impact of Blocking Cookies</h3>
                <p className="text-gray-700">
                  Blocking essential cookies will prevent you from logging in and using the platform. Blocking analytics cookies will not affect functionality but will limit our ability to improve the service.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-gray-900 mb-4">6. Do Not Track</h2>
                <p className="text-gray-700">
                  Some browsers have a "Do Not Track" (DNT) feature. Our platform does not currently respond to DNT signals, but we are committed to respecting your privacy preferences.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-gray-900 mb-4">7. Updates to This Policy</h2>
                <p className="text-gray-700">
                  We may update this Cookie Policy to reflect changes in technology or regulations. We will notify you of significant changes.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-gray-900 mb-4">8. Contact Us</h2>
                <p className="text-gray-700">
                  If you have questions about our use of cookies, contact us at support@podmixrlabs.com
                </p>
              </section>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 mt-8">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Need Help?</h3>
                <p className="text-gray-700 mb-4">If you have questions about cookies or privacy, we're here to help.</p>
                <Button
                  onClick={() => navigate('/contact')}
                  className="bg-blue-600 hover:bg-blue-700 text-white"
                >
                  Contact Support
                </Button>
              </div>
            </div>
          </Card>
        </div>
      </div>

      <Footer />
    </div>
  );
}
